import { Button, Modal } from "@adafia/ui";
import type React from "react";
import { useState } from "react";
import type { Resource } from "../../types";

interface ResourceFormProps {
	resource?: Resource;
	isOpen: boolean;
	onClose: () => void;
	onSave: (resource: Omit<Resource, "id" | "createdAt">) => Promise<void>;
}

export const ResourceForm: React.FC<ResourceFormProps> = ({
	resource,
	isOpen,
	onClose,
	onSave,
}) => {
	const [formData, setFormData] = useState({
		title: resource?.title || "",
		type: resource?.type || "blog",
		description: resource?.description || "",
		link: resource?.link || "",
		source: resource?.source || "",
	});
	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setIsSubmitting(true);
		try {
			await onSave(formData as Omit<Resource, "id" | "createdAt">);
			onClose();
		} catch (error) {
			console.error("Failed to save resource:", error);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={resource ? "Edit Resource" : "Add Resource"}
		>
			<form onSubmit={handleSubmit} className="resource-form">
				<div className="form-group">
					<label htmlFor="title">Title</label>
					<input
						id="title"
						type="text"
						className="form-input"
						value={formData.title}
						onChange={(e) =>
							setFormData({ ...formData, title: e.target.value })
						}
						required
					/>
				</div>
				<div className="form-group">
					<label htmlFor="type">Type</label>
					<select
						id="type"
						className="form-input"
						value={formData.type}
						onChange={(e) =>
							setFormData({
								...formData,
								type: e.target.value as Resource["type"],
							})
						}
					>
						<option value="blog">Article</option>
						<option value="video">Video</option>
						<option value="talk">Conference Talk</option>
					</select>
				</div>
				<div className="form-group">
					<label htmlFor="source">Source</label>
					<input
						id="source"
						type="text"
						className="form-input"
						value={formData.source}
						onChange={(e) =>
							setFormData({ ...formData, source: e.target.value })
						}
						required
						placeholder="e.g. YouTube, Medium, Personal"
					/>
				</div>
				<div className="form-group">
					<label htmlFor="link">URL</label>
					<input
						id="link"
						type="url"
						className="form-input"
						value={formData.link}
						onChange={(e) => setFormData({ ...formData, link: e.target.value })}
						required
					/>
				</div>
				<div className="form-group">
					<label htmlFor="description">Description</label>
					<textarea
						id="description"
						className="form-input"
						value={formData.description}
						onChange={(e) =>
							setFormData({ ...formData, description: e.target.value })
						}
						required
						rows={3}
					/>
				</div>
				<div className="modal-actions-refined">
					<Button type="button" variant="secondary" onClick={onClose}>
						Cancel
					</Button>
					<Button type="submit" disabled={isSubmitting}>
						{isSubmitting ? "Saving..." : "Save Resource"}
					</Button>
				</div>
			</form>
		</Modal>
	);
};
